'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Share2, Check } from 'lucide-react';
import sidebarStyles from '@/components/investigador/sidebar/sidebar.module.css';
import styles from './ShareProfileButton.module.css';

/**
 * Copies the profile's PUBLIC url to the clipboard.
 *
 * `navigator.clipboard` only exists in a secure context (HTTPS or localhost).
 * The staging deploy runs over plain HTTP, so the API is simply absent there and
 * the button would silently do nothing — hence the `execCommand` fallback.
 */
async function copyText(text: string): Promise<boolean> {
	if (navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch {
			// Permission denied or non-secure context — fall through.
		}
	}

	try {
		const textarea = document.createElement('textarea');
		textarea.value = text;
		textarea.setAttribute('readonly', '');
		textarea.style.position = 'fixed';
		textarea.style.top = '0';
		textarea.style.opacity = '0';
		document.body.appendChild(textarea);
		textarea.select();
		const copied = document.execCommand('copy');
		document.body.removeChild(textarea);
		return copied;
	} catch {
		return false;
	}
}

type CopyState = 'idle' | 'done' | 'error';

interface ShareProfileButtonProps {
	slug: string;
	/** Only changes the wording — the url is public either way. */
	isOwnProfile?: boolean;
}

export function ShareProfileButton({ slug, isOwnProfile = false }: ShareProfileButtonProps) {
	const [state, setState] = useState<CopyState>('idle');
	const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => () => {
		if (resetTimer.current) clearTimeout(resetTimer.current);
	}, []);

	const handleClick = useCallback(async () => {
		// The canonical url is always the public profile, never /mi-perfil.
		const url = `${window.location.origin}/investigadores/${encodeURIComponent(slug)}`;
		const copied = await copyText(url);

		setState(copied ? 'done' : 'error');
		if (resetTimer.current) clearTimeout(resetTimer.current);
		resetTimer.current = setTimeout(() => setState('idle'), 2000);
	}, [slug]);

	const idleLabel = isOwnProfile ? 'Copiar link de tu perfil' : 'Copiar link del perfil';
	const label =
		state === 'done' ? '¡Copiado!'
			: state === 'error' ? 'No se pudo copiar'
				: idleLabel;

	return (
		<span className={styles.shareWrap}>
			<button
				type="button"
				onClick={handleClick}
				className={`${sidebarStyles.btnGhost} ${styles.shareBtn}`}
				aria-label={idleLabel}
			>
				{state === 'done' ? <Check size={16} /> : <Share2 size={16} />}
			</button>
			<span
				className={[
					styles.tooltip,
					state !== 'idle' ? styles.tooltipPinned : '',
					state === 'done' ? styles.tooltipDone : '',
					state === 'error' ? styles.tooltipError : '',
				].filter(Boolean).join(' ')}
				// Announced on copy; the idle text is already on the button's aria-label.
				role="status"
				aria-live="polite"
			>
				{label}
			</span>
		</span>
	);
}
